import produce from 'immer';
import type { IRootDispatch, RootState } from './models';

import { selectors as userSelectors } from './CurrentUser.model';
import { selectors as providerSelectors } from './Providers.model';
import { getProviderSearchRankStats } from '../modules/Api';

import { ProviderSearchRankStats } from '../modules/Api.types';
import { PrivilegedProvider, PublicProvider } from '../api/Providers';

type Stat = {
  target: number;
  actual: number;
  startDate?: string;
  comparison: '>=' | '<=';
  raw?: {
    numerator: number;
    denominator: number;
  };
};

type CalculateStatsPayload = {
  providerId: number,
};

export type SearchRankStats = {
  clientPayments: Stat;
  newClientPayments: Stat;
  reviews: Stat;
  refunds: Stat;
  criteriaSatisfied?: number;
};

export type SearchRankState = {
  provider: PublicProvider;
  stats: SearchRankStats;
  loading: boolean;
  eligible: boolean;
};
export const MASTER_SWITCH = 'search_ranking_enabled_FLY-3206_02022022';
export const SCHEDULE_PRO_SWITCH = 'search_ranking_enabled_scheduling_FLY-3242_02222022';
const TIME_THRESHOLD_DAYS = 14;
const TOP_PRO_CRITERIA = {
  clientPayments: 65,
  newClientPayments: 90,
  reviews: 90,
  refunds: 5,
};

const model = {
  name: 'providerSearchRank',

  state: {
    provider: null,
    stats: null,
    loading: null,
    eligible: null,
  } as SearchRankState,

  reducers: {
    onLoading: produce((state: SearchRankState) => {
      state.loading = true;
    }),

    onAllLoaded: produce((state: SearchRankState) => {
      state.loading = false;
    }),

    onProviderLoaded: produce((state: SearchRankState, provider: PublicProvider) => {
      state.provider = provider;
    }),

    onStatsLoaded: produce((state: SearchRankState, stats: SearchRankStats) => {
      state.stats = stats;
    }),

    onEligibilityCalculated: produce((state: SearchRankState, eligible: boolean) => {
      state.eligible = eligible;
    }),

  },

  effects: (dispatch: IRootDispatch) => ({
    populateProvider: async (
      payload: undefined,
      rootState: RootState,
    ): Promise<PublicProvider> => {
      let provider: PublicProvider;
      dispatch.providerSearchRank.onLoading();
      const providerId: number = userSelectors.getSuperAwareProviderId(rootState);
      const hasAccessToProvider: boolean = userSelectors.hasPrivilegedAccessToProvider(
        rootState,
        providerId,
      );

      // if not provider or super user, route to home
      if (!hasAccessToProvider) {
        dispatch.route.go({ route: 'home' });
        return null;
      }

      dispatch.providers.setActiveProviderId(providerId);
      provider = providerSelectors.activeProvider(rootState);

      if (!provider) {
        provider = await dispatch.providers.loadProvider({ providerId });
      }

      dispatch.providerSearchRank.onProviderLoaded(provider);
      return provider;
    },

    calculateEligibility: async (
      payload: undefined,
      rootState: RootState,
    ) => {
      // Eligibility Handling with Switch
      //  * User must be on SS for at least 14 days
      //  * User must not have the search boost container visible
      let provider = rootState.providerSearchRank.provider as PrivilegedProvider;
      if (!provider) {
        provider = await dispatch.providerSearchRank.populateProvider() as PrivilegedProvider;
      }

      if (!rootState.abTest.isLoaded) {
        await rootState.abTest.hasLoadedPromise;
      }

      if (!rootState.abTest.assignments[
        MASTER_SWITCH
      ]?.isEnabled) {
        return dispatch.providerSearchRank.onEligibilityCalculated(false);
      }

      // check scheduling plan switch
      if (!provider.on_commission_plan && !rootState.abTest.assignments[
        SCHEDULE_PRO_SWITCH
      ]?.isEnabled) {
        return dispatch.providerSearchRank.onEligibilityCalculated(false);
      }

      // finally check criteria and assign test
      const joinedDate = Date.parse(provider.creation_time);
      const now = Date.now();
      const daysSinceJoined = (now - joinedDate)
        / (1000 * 60 * 60 * 24);

      const searchBoostExpired = provider.profile_search_boost_earning_end_date
        ? Date.parse(provider.profile_search_boost_earning_end_date) < now
        : false;

      if (
        daysSinceJoined > TIME_THRESHOLD_DAYS
        && provider.profile_phase_one_complete_date
        && searchBoostExpired
      ) {
        return dispatch.providerSearchRank.onEligibilityCalculated(true);
      }
      return dispatch.providerSearchRank.onEligibilityCalculated(false);
    },
    calculateStats: async (
      payload: CalculateStatsPayload,
    ) => {
      const stats: ProviderSearchRankStats = await getProviderSearchRankStats(payload.providerId);
      const {
        completedAppointmentCount: appointments,
        completedOnStyleseatAppointmentCount: payments,
        dateRangeStart,
        ncdCompletedAppointmentCount: ncdAppointments,
        ncdCompletedOnStyleseatAppointmentCount: ncdPayments,
        ncdDateRangeStart,
        reviewTotalCount: allReviews,
        reviewPositiveCount: positiveReviews,
        reviewDateRangeStart,
        ncdRefundedAppointmentCount: refundedCount,
      } = stats;
      const calculatedStats: SearchRankStats = {
        clientPayments: {
          actual: Math.round((payments / appointments) * 100),
          target: TOP_PRO_CRITERIA.clientPayments,
          comparison: '>=',
          startDate: dateRangeStart,
          raw: {
            numerator: payments,
            denominator: appointments,
          },
        },
        newClientPayments: {
          actual: Math.round((ncdPayments / ncdAppointments) * 100),
          target: TOP_PRO_CRITERIA.newClientPayments,
          startDate: ncdDateRangeStart,
          comparison: '>=',
          raw: {
            numerator: ncdPayments,
            denominator: ncdAppointments,
          },
        },
        reviews: {
          actual: Math.round((positiveReviews / allReviews) * 100),
          target: TOP_PRO_CRITERIA.reviews,
          startDate: reviewDateRangeStart,
          comparison: '>=',
          raw: {
            numerator: positiveReviews,
            denominator: allReviews,
          },
        },
        refunds: {
          actual: Math.round((refundedCount / ncdPayments) * 100),
          target: TOP_PRO_CRITERIA.refunds,
          startDate: ncdDateRangeStart,
          comparison: '<=',
          raw: {
            numerator: refundedCount,
            denominator: ncdAppointments,
          },
        },
      };

      const criteriaSatisfied = Object
        .keys(calculatedStats)
        .reduce((previous: number, current: string) => {
          const stat = calculatedStats[current];

          if (stat.comparison === '>=') {
            if (stat.actual >= stat.target) {
              return previous + 2;
            }

            if (stat.actual >= (stat.target / 2)) {
              return previous + 1;
            }
          }

          if (stat.comparison === '<=') {
            if (stat.actual <= stat.target) {
              return previous + 2;
            }

            if (stat.actual <= (stat.target / 2)) {
              return previous + 1;
            }
          }

          return previous;
        }, 0);

      const parsedStats = {
        ...calculatedStats,
        criteriaSatisfied,
      };

      dispatch.providerSearchRank.onStatsLoaded(parsedStats);

      return parsedStats;
    },
    land: async () => {
      const { id: providerId } = await dispatch.providerSearchRank.populateProvider();
      await dispatch.providerSearchRank.calculateStats({ providerId });
      await dispatch.providerSearchRank.calculateEligibility();
      dispatch.providerSearchRank.onAllLoaded();
      dispatch.loader.setIsLoading(false);
    },
  }),

  selectors: slice => ({
    pageData() {
      return slice((state: SearchRankState) => state);
    },
    eligible() {
      return slice((state: SearchRankState) => state.eligible);
    },
  }),
};

export default model;
